import {
  generateMedicalExpensePdf,
  generateMovementPdf,
  generateFullBreakdownPdf,
  generatePartialBreakdownPdf,
  generateOtherIncomePdf,
  generateDonationPdf,
  generateFurtherDeductionPdf,
  generateSharedBasedPaymentPdf,
  generateSavvyPdf,
  generateRentalIncomePdf,
  generateInterestExpensePdf,
  generateRenovationPdf,
  generateAllocationExpensesPdf,
  generateInvestIncomePdf
} from "../../Schedules/Others/Pdf/generator";
import JSZip from "jszip";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { isNotEmptyObject } from "../../../utils/helpers";
import {
  generateCaSchedulePdf,
  generateCaSummaryPdf,
  generateFaAdditionsPdf,
  generateFaDisposalPdf,
  generateFaRegisterPdf,
  generateFaSummaryPdf,
  generateHirePurchasePdf,
  generateS19SchedulePdf
} from "../../Schedules/CA/Pdf/generator";
import { initializePDF } from "../../../utils/hooks/pdfHandlers";
import { PDFPortraitScheduleConstants } from "../../../utils/static/pdfConstants";
import autoTable from "jspdf-autotable";
import { addItalicText, buildCompanyHeader } from "../../../utils/pdf/page";
import { generateDetailedPlPdf } from "../../DetailedP&L/PdfGenerator";
import { generateMainCompPdf } from "../../MainTaxComputation/Pdf/generateMainCompPdf";
import { BaseExcelGenerator } from "../../../utils/excel/BaseExcelGenerator";
import { BasePdfGenerator } from "../../../utils/pdf/BasePdfGenerator";
import {
  generateFtcPdf,
  generateUnremittedForeignPdf
} from "../../Schedules/ForeignIncomeTracker/Pdf/generator";
import {
  calculateSharedBasedPayment
} from "../../Schedules/Others/SharedBasedPayement/sharedBasedPaymentCalculation";
import { getCachedUserPreference } from "../../../contexts/services/userPreferenceServices";
import {
  DEFAULT_PDF_FOOTER_OPTIONS,
  formatPdfPageNumber,
  normalizePdfFooterOptions
} from "./pdfFooterOptions";

const generateTocPdf = (forView, company, toc) => {
  const doc = initializePDF();
  const leftX = PDFPortraitScheduleConstants.leftX;
  const initY = PDFPortraitScheduleConstants.initY;
  const fs = 10;
  let startY = initY;

  // Add company header
  startY = buildCompanyHeader(company, "Content Page", doc, leftX, startY, fs);

  // Prepare table data
  const contentData = toc.map((item) => [item.scheduleName, item.scheduleNo]);

  // Generate table
  autoTable(doc, {
    startY,
    head: [],
    body: contentData,
    theme: "plain",
    styles: {
      fontSize: fs
    },
    headStyles: {
      fontStyle: "bold",
      halign: "center",
      textColor: [0, 0, 0]
    },
    bodyStyles: {
      cellPadding: { top: 3, right: 3, bottom: 1, left: 3 }
    },
    columnStyles: {
      0: { halign: "left", cellWidth: 100 },
      1: { halign: "center", minCellWidth: 30 }
    }
  });

  if (forView) {
    startY = doc.lastAutoTable.finalY + 10;

    addItalicText(
      doc,
      "Note: Only saved statements and schedules will be included in the download.",
      leftX,
      startY
    );
  }

  return doc;
};

const generateTocExcel = (excel, forView, company, toc) => {
  const schedule = { name: "Content Page" };
  const contentData = toc.map((item) => [item.scheduleName, item.scheduleNo]);

  excel.initializeHeader(company, schedule, "Content Page");
  excel.buildAutoTable({
    head: [],
    body: contentData,
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "center" }
    },
    schedule,
    company,
    lineSettings: {
      labelRows: [],
      totalRows: [],
      subTotalRows: [],
      totalLines: [],
      subTotalLines: [],
      totalDoubleLines: [],
      subTotalDoubleLines: []
    }
  });

  if (forView) {
    const sheet = excel.getCurrentSheet();
    excel.currentRow++;

    const noteRow = sheet.getRow(excel.currentRow);
    noteRow.getCell(1).value =
      "Note: Only saved statements and schedules will be included in the download.";
    noteRow.getCell(1).style = {
      ...excel.settings.styles.notes,
      font: {
        ...excel.settings.styles.notes.font,
        italic: true
      }
    };
    sheet.mergeCells(excel.currentRow, 1, excel.currentRow, 2);
    noteRow.height = 18;
    excel.autoFitColumns(sheet);
  }
};

const buildMainCompCompanyForPdf = (savedCompany, fallbackCompany) => ({
  ...(fallbackCompany || {}),
  ...(savedCompany || {}),
  financialYearEnd:
    fallbackCompany?.financialYearEnd ?? savedCompany?.financialYearEnd,
  financialYearStart:
    fallbackCompany?.financialYearStart ?? savedCompany?.financialYearStart,
  taxPeriodType: fallbackCompany?.taxPeriodType ?? savedCompany?.taxPeriodType,
  basisPeriods: fallbackCompany?.basisPeriods ?? savedCompany?.basisPeriods,
  isCustomizedBasisPeriod:
    fallbackCompany?.isCustomizedBasisPeriod ??
    savedCompany?.isCustomizedBasisPeriod,
  tc_id: fallbackCompany?.tc_id ?? savedCompany?.tc_id,
  yearOfAssessment:
    fallbackCompany?.yearOfAssessment ?? savedCompany?.yearOfAssessment
});

export { DEFAULT_PDF_FOOTER_OPTIONS, normalizePdfFooterOptions };

const getPdfSafeText = (text, font) =>
  Array.from(text)
    .map((character) => {
      try {
        font.encodeText(character);
        return character;
      } catch {
        return "?";
      }
    })
    .join("");

const fitFooterText = (text, font, fontSize, maxWidth) => {
  const safeText = getPdfSafeText(text, font);
  if (!safeText || font.widthOfTextAtSize(safeText, fontSize) <= maxWidth) {
    return safeText;
  }

  const ellipsis = "...";
  const ellipsisWidth = font.widthOfTextAtSize(ellipsis, fontSize);
  let low = 0;
  let high = safeText.length;

  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    const candidate = safeText.slice(0, middle);
    const candidateWidth = font.widthOfTextAtSize(candidate, fontSize);

    if (candidateWidth + ellipsisWidth <= maxWidth) low = middle;
    else high = middle - 1;
  }

  return `${safeText.slice(0, low).trimEnd()}${ellipsis}`;
};

const addFooterToMergedPdf = async (
  pdf,
  footerOptions,
  contentPageCount = 0
) => {
  const footer = normalizePdfFooterOptions(footerOptions);
  if (!footer.enabled && !footer.pageNumber.enabled) return;

  const footerFont = await pdf.embedFont(StandardFonts.HelveticaBold);
  const footerFontSize = 8;
  const horizontalMargin = 18;
  const slotGap = 8;

  const pages = pdf.getPages();
  const excludedContentPageCount = footer.includeContentPage
    ? 0
    : Math.min(contentPageCount, pages.length);
  const numberedPageCount = pages.length - excludedContentPageCount;

  pages.forEach((page, pageIndex) => {
    if (pageIndex < excludedContentPageCount) return;

    const { width } = page.getSize();
    const availableWidth = width - horizontalMargin * 2 - slotGap * 2;
    const slotWidth = availableWidth / 3;
    const pageNumberText = footer.pageNumber.enabled
      ? formatPdfPageNumber({
          pageNumber: pageIndex - excludedContentPageCount + 1,
          pageCount: numberedPageCount,
          style: footer.pageNumber.style
        })
      : "";
    const slots = [
      { alignment: "left", text: footer.enabled ? footer.leftText : "" },
      { alignment: "center", text: footer.enabled ? footer.centerText : "" },
      { alignment: "right", text: footer.enabled ? footer.rightText : "" }
    ].map((slot) => ({
      ...slot,
      text:
        footer.pageNumber.enabled &&
        footer.pageNumber.position === slot.alignment
          ? [slot.text, pageNumberText].filter(Boolean).join(" | ")
          : slot.text
    }));

    slots.forEach(({ alignment, text }, index) => {
      if (!text) return;

      const fittedText = fitFooterText(
        text,
        footerFont,
        footerFontSize,
        slotWidth
      );
      const textWidth = footerFont.widthOfTextAtSize(
        fittedText,
        footerFontSize
      );
      const slotStart = horizontalMargin + index * (slotWidth + slotGap);
      const x =
        alignment === "center"
          ? slotStart + (slotWidth - textWidth) / 2
          : alignment === "right"
            ? slotStart + slotWidth - textWidth
            : slotStart;

      page.drawText(fittedText, {
        x,
        y: 14,
        size: footerFontSize,
        color: rgb(0.35, 0.35, 0.35)
      });
    });
  });
};

async function mergePDFs(
  blobs,
  fileName,
  { download = true, footer, contentPageCount = 0 } = {}
) {
  const mergedPdf = await PDFDocument.create();

  for (const blob of blobs) {
    const arrayBuffer = await blob.arrayBuffer();
    const pdf = await PDFDocument.load(arrayBuffer);
    const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
    copiedPages.forEach((page) => mergedPdf.addPage(page));
  }

  await addFooterToMergedPdf(mergedPdf, footer, contentPageCount);

  const mergedPdfBytes = await mergedPdf.save();
  const mergedBlob = new Blob([mergedPdfBytes], { type: "application/pdf" });

  if (download) {
    const url = URL.createObjectURL(mergedBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName}.pdf`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  return {
    blob: mergedBlob,
    fileName: `${fileName}.pdf`
  };
}

function recordSavedDoc({
  doc,
  updatedAt,
  scheduleNo,
  scheduleType,
  scheduleName,
  zip,
  pdfBlobs
}) {
  if (!doc) return;

  const date = new Date(updatedAt);
  const formattedDate = date.toLocaleDateString("en-GB").replace(/\//g, "");
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const formattedTime = `${hours}${minutes}`;

  let filename = `Sch${scheduleNo}_${scheduleType}_${scheduleName}_v${formattedDate}_${formattedTime}.pdf`;

  if (scheduleType === "ContentPage") {
    filename = "Content Page.pdf";
  } else if (scheduleType === "detailPl") {
    filename = "Detailed Income Statement.pdf";
  } else if (scheduleType === "mainComp") {
    filename = "Main Computation.pdf";
  }

  const pdfBlob = doc.output("blob");

  pdfBlobs.push(pdfBlob);
  zip.file(filename, pdfBlob);
}

export const downloadAllFromJson = async (
  company,
  toc,
  rawMainComp,
  rawDetailPl,
  rawPlSchedules,
  rawCaSchedules,
  fileName = "Tax Computation",
  downloadType = "zip",
  forView = false,
  onError,
  options = {}
) => {
  // Excel download path (single workbook with tabs)
  if (downloadType === "excel") {
    const excel = new BaseExcelGenerator();
    BasePdfGenerator.setSharedExcel(excel, { autoDownload: false });

    try {
      const runInNewSheet = async (fn) => {
        excel.resetCurrentSheet();
        return await Promise.resolve(fn());
      };

      if (toc && toc.length > 0) {
        try {
          generateTocExcel(excel, forView, company, toc);
        } catch (e) {
          if (onError)
            onError(
              "Content Page",
              `Failed to generate Content Page Excel: ${e?.message || e}`
            );
        }
      }

      // Main computation
      if (rawMainComp && rawMainComp.jsonData) {
        try {
          const {
            company: mainCompany,
            currency,
            currencyRate,
            netProfitBeforeTax,
            referenceData,
            unutilisedCapitalAllowances,
            unutilisedLosses,
            currentYearLosses,
            unutilisedDonations,
            corporateIncomeTaxRebate,
            currentYearRebate,
            isStartup,
            taxPreviouslyAssessed,
            computedSummary,
            unabsorbedSummary,
            crossYAApportionmentContext,
            comments,
            reviewNote
          } = JSON.parse(rawMainComp.jsonData);
          const resolvedMainCompany = buildMainCompCompanyForPdf(
            mainCompany,
            company
          );

          await runInNewSheet(() =>
            generateMainCompPdf(
              forView,
              resolvedMainCompany,
              currency,
              currencyRate,
              netProfitBeforeTax,
              referenceData,
              unutilisedCapitalAllowances,
              unutilisedLosses,
              currentYearLosses,
              unutilisedDonations,
              corporateIncomeTaxRebate,
              currentYearRebate,
              isStartup,
              taxPreviouslyAssessed,
              computedSummary,
              unabsorbedSummary,
              crossYAApportionmentContext,
              comments,
              reviewNote,
              true
            )
          );
        } catch (e) {
          if (onError)
            onError(
              "Main Computation",
              `Failed to generate Main Computation Excel: ${e?.message || e}`
            );
        }
      }

      // Detailed P&L
      if (rawDetailPl && rawDetailPl.jsonData) {
        try {
          const {
            company: plCompany,
            revenue,
            otherIncome,
            costOfSales,
            expenses,
            sectionTotals,
            currencyView,
            notes,
            reviewNotes,
            flipSignMode
          } = JSON.parse(rawDetailPl.jsonData);

          await runInNewSheet(() =>
            generateDetailedPlPdf(
              forView,
              plCompany,
              revenue,
              otherIncome,
              costOfSales,
              expenses,
              sectionTotals,
              currencyView,
              notes,
              reviewNotes,
              flipSignMode,
              true
            )
          );
        } catch (e) {
          if (onError)
            onError(
              "Detailed P&L",
              `Failed to generate Detailed Income Statement Excel: ${
                e?.message || e
              }`
            );
        }
      }

      // P&L schedules
      if (rawPlSchedules && rawPlSchedules.length > 0) {
        for (const {
          scheduleNo,
          scheduleName,
          scheduleType,
          jsonData
        } of rawPlSchedules) {
          try {
            if (isNotEmptyObject(jsonData)) {
              switch (scheduleType) {
                case "Interest_Expense": {
                  const {
                    interestExpenseRows,
                    assetsRows,
                    totalInterestExpenses,
                    totalAssets,
                    totalNonIncomeAssets,
                    disAllowableInterest,
                    notes,
                    reviewNotes
                  } = JSON.parse(jsonData);

                  generateInterestExpensePdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo
                    },
                    company,
                    interestExpenseRows,
                    assetsRows,
                    totalInterestExpenses,
                    totalAssets,
                    totalNonIncomeAssets,
                    disAllowableInterest,
                    notes,
                    reviewNotes,
                    true
                  );
                  break;
                }
                case "Medical_Expense": {
                  const {
                    employeeRemunerationRows,
                    medicalExpenseRows,
                    totalEmployeeRemuneration,
                    totalMedicalExpense,
                    disallowableExpenses,
                    expenseCappedAt,
                    expenseCappedAmount,
                    notes,
                    reviewNotes
                  } = JSON.parse(jsonData);

                  generateMedicalExpensePdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo
                    },
                    company,
                    employeeRemunerationRows,
                    medicalExpenseRows,
                    totalEmployeeRemuneration,
                    totalMedicalExpense,
                    disallowableExpenses,
                    expenseCappedAt,
                    expenseCappedAmount,
                    notes,
                    reviewNotes,
                    true
                  );
                  break;
                }
                case "Movement": {
                  const {
                    openingBalance,
                    movementBalance,
                    closingBalance,
                    movementLines,
                    notes,
                    reviewNotes
                  } = JSON.parse(jsonData);

                  generateMovementPdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo
                    },
                    company,
                    openingBalance,
                    movementBalance,
                    closingBalance,
                    movementLines,
                    notes,
                    reviewNotes,
                    true
                  );
                  break;
                }
                case "Full_Breakdown": {
                  const {
                    sourceData,
                    grossBalance,
                    adjustedTotalBalance,
                    totalSourceAmount,
                    lines,
                    notes,
                    reviewNotes
                  } = JSON.parse(jsonData);

                  generateFullBreakdownPdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo
                    },
                    company,
                    sourceData,
                    grossBalance,
                    adjustedTotalBalance,
                    totalSourceAmount,
                    lines,
                    notes,
                    reviewNotes,
                    true
                  );
                  break;
                }
                case "Partial_Breakdown": {
                  const {
                    grossBalance,
                    adjustedTotalBalance,
                    lines,
                    notes,
                    reviewNotes
                  } = JSON.parse(jsonData);

                  generatePartialBreakdownPdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo
                    },
                    company,
                    grossBalance,
                    adjustedTotalBalance,
                    lines,
                    notes,
                    reviewNotes,
                    true
                  );
                  break;
                }
                case "Foreign_Trade":
                case "Other_Income": {
                  const {
                    taxAdjustment,
                    otherIncomeRows,
                    expensesRows,
                    totalOtherIncome,
                    totalExpenses,
                    netOtherIncome,
                    foreignTaxSettings,
                    notes,
                    reviewNotes
                  } = JSON.parse(jsonData);

                  generateOtherIncomePdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo,
                      taxAdjustment
                    },
                    company,
                    otherIncomeRows,
                    expensesRows,
                    totalOtherIncome,
                    totalExpenses,
                    netOtherIncome,
                    foreignTaxSettings,
                    notes,
                    reviewNotes,
                    true
                  );
                  break;
                }
                case "Unremitted_Foreign_Income": {
                  const {
                    taxAdjustment,
                    currentYearUnremittedForeignRows,
                    previousYearUnremittedForeignRows,
                    foreignSourceTrackingRows,
                    trackingSubtotals,
                    summaryData,
                    foreignTaxCreditScheduleRef,
                    notes,
                    reviewNotes
                  } = JSON.parse(jsonData);

                  generateUnremittedForeignPdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo,
                      taxAdjustment
                    },
                    company,
                    currentYearUnremittedForeignRows,
                    previousYearUnremittedForeignRows,
                    foreignSourceTrackingRows,
                    trackingSubtotals,
                    summaryData,
                    foreignTaxCreditScheduleRef,
                    notes,
                    reviewNotes,
                    true
                  );
                  break;
                }
                case "Foreign_Tax_Credit": {
                  const {
                    taxAdjustment,
                    functionalCurrency,
                    functionalCurrencyRate,
                    breakdownRows,
                    foreignIncomeRows,
                    foreignIncomeTotals,
                    ftcCalculatedData,
                    notes,
                    reviewNotes
                  } = JSON.parse(jsonData);

                  generateFtcPdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo,
                      taxAdjustment
                    },
                    company,
                    functionalCurrency,
                    functionalCurrencyRate,
                    breakdownRows,
                    foreignIncomeRows,
                    foreignIncomeTotals,
                    ftcCalculatedData,
                    notes,
                    reviewNotes,
                    true
                  );
                  break;
                }
                case "Donation": {
                  const {
                    donationRows,
                    totalDonation,
                    additionDeduction,
                    additionDeductionAmount,
                    notes,
                    reviewNotes
                  } = JSON.parse(jsonData);

                  generateDonationPdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo
                    },
                    company,
                    donationRows,
                    totalDonation,
                    additionDeduction,
                    additionDeductionAmount,
                    notes,
                    reviewNotes,
                    true
                  );
                  break;
                }
                case "Further_Deduction": {
                  const {
                    expensesRows,
                    totalExpenses,
                    isExpenseCapped,
                    deductionRate,
                    expenseCappedAmount,
                    finalDeduction,
                    notes,
                    reviewNotes,
                    ...deductionDetails
                  } = JSON.parse(jsonData);

                  generateFurtherDeductionPdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo
                    },
                    company,
                    expensesRows,
                    totalExpenses,
                    isExpenseCapped,
                    deductionRate,
                    expenseCappedAmount,
                    finalDeduction,
                    notes,
                    reviewNotes,
                    true,
                    deductionDetails
                  );
                  break;
                }
                case "Share_Based_Payment": {
                  const scheduleData = JSON.parse(jsonData);
                  const calculationResults =
                    calculateSharedBasedPayment(scheduleData);

                  generateSharedBasedPaymentPdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo
                    },
                    company,
                    scheduleData,
                    calculationResults,
                    scheduleData.notes,
                    scheduleData.reviewNotes,
                    true,
                    scheduleData.showFifoShares
                  );
                  break;
                }
                case "Savy":
                case "Investment_Savy": {
                  const { scheduleData, notes, reviewNotes, taxAdjustment } =
                    JSON.parse(jsonData);

                  generateSavvyPdf(
                    forView,
                    company,
                    {
                      name: scheduleName,
                      scheduleNo,
                      taxAdjustment
                    },
                    scheduleData,
                    notes,
                    reviewNotes,
                    true
                  );
                  break;
                }
                case "Rental": {
                  const {
                    taxAdjustment,
                    address,
                    hasVacantPeriod,
                    vacantPeriod,
                    rentalPeriod,
                    grossIncomeRows,
                    expensesRows,
                    totalGrossIncome,
                    netRentalIncome,
                    foreignTaxSettings,
                    notes,
                    reviewNotes,
                    shareOfExpenses
                  } = JSON.parse(jsonData);

                  generateRentalIncomePdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo,
                      taxAdjustment
                    },
                    company,
                    address,
                    hasVacantPeriod,
                    vacantPeriod,
                    rentalPeriod,
                    grossIncomeRows,
                    expensesRows,
                    totalGrossIncome,
                    netRentalIncome,
                    foreignTaxSettings,
                    notes,
                    reviewNotes,
                    shareOfExpenses,
                    true
                  );
                  break;
                }
                case "Renovation": {
                  const {
                    rennovationRows,
                    mode,
                    isSdg,
                    tableRows,
                    secondTableRows,
                    totalRennovations,
                    totalQualifyingCost,
                    totalQualifyingCostBF,
                    totalCostIncurred,
                    totalDeduction,
                    totalCostCF,
                    totalSecondCostIncurred,
                    totalSecondQualifyingCost,
                    totalSecondQualifyingCostBF,
                    totalSecondDeduction,
                    totalSecondCostCF,
                    notes,
                    reviewNotes
                  } = JSON.parse(jsonData);

                  generateRenovationPdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo
                    },
                    company,
                    rennovationRows,
                    mode,
                    isSdg,
                    tableRows,
                    secondTableRows,
                    totalRennovations,
                    totalQualifyingCost,
                    totalQualifyingCostBF,
                    totalCostIncurred,
                    totalDeduction,
                    totalCostCF,
                    totalSecondCostIncurred,
                    totalSecondQualifyingCost,
                    totalSecondQualifyingCostBF,
                    totalSecondDeduction,
                    totalSecondCostCF,
                    notes,
                    reviewNotes,
                    true
                  );
                  break;
                }
                case "Allocation_Of_Expenses": {
                  const {
                    incomeRows,
                    statutoryExpensesRows,
                    commonExpensesRows,
                    cappedRate,
                    expenseCappedAmount,
                    cappedVsCommon,
                    finalAllowedExpenses,
                    deductibleAllocationRows,
                    totalDeductibleExpenses,
                    notes,
                    reviewNotes
                  } = JSON.parse(jsonData);

                  generateAllocationExpensesPdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo
                    },
                    company,
                    incomeRows,
                    statutoryExpensesRows,
                    commonExpensesRows,
                    cappedRate,
                    expenseCappedAmount,
                    cappedVsCommon,
                    finalAllowedExpenses,
                    deductibleAllocationRows,
                    totalDeductibleExpenses,
                    notes,
                    reviewNotes,
                    true
                  );
                  break;
                }
                case "Other_Investment_Income": {
                  const {
                    taxAdjustment,
                    investIncomeRows,
                    expensesRows,
                    totalInvestIncome,
                    totalExpenses,
                    netIncome,
                    foreignTaxSettings,
                    shareOfExpenses,
                    notes,
                    reviewNotes
                  } = JSON.parse(jsonData);

                  generateInvestIncomePdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo,
                      taxAdjustment
                    },
                    company,
                    investIncomeRows,
                    expensesRows,
                    totalInvestIncome,
                    totalExpenses,
                    netIncome,
                    foreignTaxSettings,
                    shareOfExpenses,
                    notes,
                    reviewNotes,
                    true
                  );
                  break;
                }
                default:
                  break;
              }
            }
          } catch (e) {
            if (onError)
              onError(
                `PL schedule ${scheduleNo} (${scheduleType})`,
                `Failed to generate PL schedule Excel ${scheduleNo} (${scheduleType}): ${
                  e?.message || e
                }`
              );
          }
        }
      }

      // CA schedules
      if (rawCaSchedules && rawCaSchedules.length > 0) {
        for (const {
          scheduleNo,
          scheduleName,
          scheduleType,
          jsonData
        } of rawCaSchedules) {
          try {
            if (isNotEmptyObject(jsonData)) {
              switch (scheduleType) {
                case "FASummary": {
                  const {
                    costRows,
                    accDepreciationRows,
                    totalOpening,
                    totalAdditions,
                    totalDisposal,
                    totalClosing,
                    notes,
                    reviewNotes
                  } = JSON.parse(jsonData);

                  generateFaSummaryPdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo
                    },
                    company,
                    costRows,
                    accDepreciationRows,
                    totalOpening,
                    totalAdditions,
                    totalDisposal,
                    totalClosing,
                    notes,
                    reviewNotes,
                    true
                  );
                  break;
                }
                case "FARegister": {
                  const { assetRows, dataRows, notes, reviewNotes } =
                    JSON.parse(jsonData);

                  generateFaRegisterPdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo
                    },
                    company,
                    assetRows || dataRows || [],
                    notes,
                    reviewNotes,
                    true
                  );
                  break;
                }
                case "FAAdditions": {
                  const {
                    assetLines,
                    otherSources,
                    registeredAssetSources,
                    totalLines,
                    totalAdditions,
                    otherSourcesTotalLines,
                    registeredAssetSourcesTotalLines,
                    combinedTotalLines,
                    filteredUsedColumns,
                    caSettings,
                    refSources,
                    notes,
                    reviewNotes,
                    renovationScheduleNo
                  } = JSON.parse(jsonData);

                  generateFaAdditionsPdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo
                    },
                    company,
                    assetLines,
                    otherSources,
                    registeredAssetSources,
                    totalLines,
                    totalAdditions,
                    otherSourcesTotalLines,
                    registeredAssetSourcesTotalLines,
                    combinedTotalLines,
                    filteredUsedColumns,
                    caSettings,
                    refSources,
                    notes,
                    reviewNotes,
                    renovationScheduleNo,
                    true
                  );
                  break;
                }
                case "FADisposals": {
                  const {
                    assetLines,
                    totalAssetLines,
                    showNetbookValue,
                    twdvSummary,
                    bcSummary,
                    refSources = {},
                    notes,
                    reviewNotes
                  } = JSON.parse(jsonData);

                  generateFaDisposalPdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo
                    },
                    company,
                    assetLines,
                    totalAssetLines,
                    showNetbookValue,
                    twdvSummary,
                    bcSummary,
                    refSources,
                    notes,
                    reviewNotes,
                    true
                  );
                  break;
                }
                case "S19ASchedule":
                case "S19BSchedule":
                case "S19DSchedule":
                case "S16Schedule": {
                  const {
                    caType,
                    refSources,
                    additionsDetails,
                    disposalDetails,
                    hirePurchaseAdditions,
                    hirePurchaseDisposal,
                    totalAdditionsDetails,
                    totalDisposalDetails,
                    totalHpAdditionsDetails,
                    totalHpDisposalDetails,
                    totalNetAA,
                    caSummaryDetails,
                    notes,
                    reviewNotes
                  } = JSON.parse(jsonData);

                  generateCaSchedulePdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo
                    },
                    company,
                    caType,
                    refSources,
                    additionsDetails,
                    disposalDetails,
                    hirePurchaseAdditions,
                    hirePurchaseDisposal,
                    totalAdditionsDetails,
                    totalDisposalDetails,
                    totalHpAdditionsDetails,
                    totalHpDisposalDetails,
                    totalNetAA,
                    caSummaryDetails,
                    notes,
                    reviewNotes,
                    true
                  );
                  break;
                }
                case "S19Schedule": {
                  const {
                    caType,
                    refSources,
                    additionsDetails,
                    disposalDetails,
                    hirePurchaseAdditions,
                    hirePurchaseDisposal,
                    totalAdditionsDetails,
                    totalDisposalDetails,
                    totalHpAdditionsDetails,
                    totalHpDisposalDetails,
                    totalNetAA,
                    caSummaryDetails,
                    notes,
                    reviewNotes
                  } = JSON.parse(jsonData);

                  generateS19SchedulePdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo
                    },
                    company,
                    caType,
                    refSources,
                    additionsDetails,
                    disposalDetails,
                    hirePurchaseAdditions,
                    hirePurchaseDisposal,
                    totalAdditionsDetails,
                    totalDisposalDetails,
                    totalHpAdditionsDetails,
                    totalHpDisposalDetails,
                    totalNetAA,
                    caSummaryDetails,
                    notes,
                    reviewNotes,
                    true
                  );
                  break;
                }
                case "HirePurchase": {
                  const {
                    assets,
                    assetCodeList,
                    summaryData,
                    caTypeTotals,
                    notes,
                    reviewNotes
                  } = JSON.parse(jsonData);

                  generateHirePurchasePdf(
                    forView,
                    {
                      name: scheduleName,
                      scheduleNo
                    },
                    company,
                    assets,
                    assetCodeList,
                    summaryData,
                    caTypeTotals,
                    notes,
                    reviewNotes,
                    true
                  );
                  break;
                }
                case "CASummary": {
                  generateCaSummaryPdf(
                    forView,
                    {
                      type: "CA Summary",
                      scheduleNo,
                      name: "CA Summary"
                    },
                    company,
                    jsonData,
                    jsonData.reduce((sum, row) => sum + row.total, 0),
                    "",
                    "",
                    true
                  );
                  break;
                }
                default:
                  break;
              }
            }
          } catch (e) {
            if (onError)
              onError(
                `CA schedule ${scheduleNo} (${scheduleType})`,
                `Failed to generate CA schedule Excel ${scheduleNo} (${scheduleType}): ${
                  e?.message || e
                }`
              );
          }
        }
      }

      const finalName = `${forView ? "Draft_" : ""}${fileName}.xlsx`;
      await excel.downloadAs(finalName);
    } finally {
      BasePdfGenerator.clearSharedExcel();
    }

    return;
  }

  const zip = new JSZip();
  const pdfBlobs = [];
  let contentPageCount = 0;

  if (toc && toc.length > 0) {
    // Generate TOC PDF first
    try {
      const tocDoc = generateTocPdf(forView, company, toc);
      recordSavedDoc({
        doc: tocDoc,
        updatedAt: new Date().toISOString(),
        scheduleNo: null,
        scheduleType: "ContentPage",
        scheduleName: "Content Page",
        zip,
        pdfBlobs
      });
      contentPageCount = tocDoc.getNumberOfPages();
    } catch (e) {
      if (onError)
        onError(
          "Content Page",
          `Failed to generate Content Page: ${e?.message || e}`
        );
    }

    if (rawMainComp && rawMainComp.jsonData) {
      let doc = null;
      try {
        const cachedPdfPreference =
          getCachedUserPreference()?.pdf?.settings || {};
        const {
          company: mainCompany,
          currency,
          currencyRate,
          netProfitBeforeTax,
          referenceData,
          unutilisedCapitalAllowances,
          unutilisedLosses,
          currentYearLosses,
          unutilisedDonations,
          corporateIncomeTaxRebate,
          currentYearRebate,
          isStartup,
          taxPreviouslyAssessed,
          computedSummary,
          unabsorbedSummary,
          crossYAApportionmentContext,
          comments,
          reviewNote
        } = JSON.parse(rawMainComp.jsonData);
        const resolvedMainCompany = buildMainCompCompanyForPdf(
          mainCompany,
          company
        );

        doc = await Promise.resolve(
          generateMainCompPdf(
            forView,
            resolvedMainCompany,
            currency,
            currencyRate,
            netProfitBeforeTax,
            referenceData,
            unutilisedCapitalAllowances,
            unutilisedLosses,
            currentYearLosses,
            unutilisedDonations,
            corporateIncomeTaxRebate,
            currentYearRebate,
            isStartup,
            taxPreviouslyAssessed,
            computedSummary,
            unabsorbedSummary,
            crossYAApportionmentContext,
            comments,
            reviewNote,
            false,
            cachedPdfPreference
          )
        );
      } catch (e) {
        if (onError)
          onError(
            "Main Computation",
            `Failed to generate Main Computation: ${e?.message || e}`
          );
      }

      recordSavedDoc({
        doc,
        updatedAt: rawMainComp.updatedAt,
        scheduleNo: null,
        scheduleType: "mainComp",
        scheduleName: "",
        zip,
        pdfBlobs
      });
    }

    if (rawDetailPl && rawDetailPl.jsonData) {
      let doc = null;
      try {
        const cachedPdfPreference =
          getCachedUserPreference()?.pdf?.settings || {};
        const {
          company,
          revenue,
          otherIncome,
          costOfSales,
          expenses,
          sectionTotals,
          currencyView,
          notes,
          reviewNotes,
          flipSignMode
        } = JSON.parse(rawDetailPl.jsonData);

        doc = generateDetailedPlPdf(
          forView,
          company,
          revenue,
          otherIncome,
          costOfSales,
          expenses,
          sectionTotals,
          currencyView,
          notes,
          reviewNotes,
          flipSignMode,
          false,
          cachedPdfPreference
        );
      } catch (e) {
        if (onError)
          onError(
            "Detailed P&L",
            `Failed to generate Detailed Income Statement: ${e?.message || e}`
          );
      }

      recordSavedDoc({
        doc,
        updatedAt: rawDetailPl.updatedAt,
        scheduleNo: null,
        scheduleType: "detailPl",
        scheduleName: "",
        zip,
        pdfBlobs
      });
    }

    if (rawPlSchedules && rawPlSchedules.length > 0) {
      for (const {
        scheduleNo,
        scheduleName,
        scheduleType,
        updatedAt,
        jsonData
      } of rawPlSchedules) {
        let doc = null;
        try {
          if (isNotEmptyObject(jsonData)) {
            switch (scheduleType) {
              case "Interest_Expense": {
                const {
                  interestExpenseRows,
                  assetsRows,
                  totalInterestExpenses,
                  totalAssets,
                  totalNonIncomeAssets,
                  disAllowableInterest,
                  notes,
                  reviewNotes
                } = JSON.parse(jsonData);

                doc = generateInterestExpensePdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo
                  },
                  company,
                  interestExpenseRows,
                  assetsRows,
                  totalInterestExpenses,
                  totalAssets,
                  totalNonIncomeAssets,
                  disAllowableInterest,
                  notes,
                  reviewNotes
                );
                break;
              }
              case "Medical_Expense": {
                const {
                  employeeRemunerationRows,
                  medicalExpenseRows,
                  totalEmployeeRemuneration,
                  totalMedicalExpense,
                  disallowableExpenses,
                  expenseCappedAt,
                  expenseCappedAmount,
                  notes,
                  reviewNotes
                } = JSON.parse(jsonData);

                doc = generateMedicalExpensePdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo
                  },
                  company,
                  employeeRemunerationRows,
                  medicalExpenseRows,
                  totalEmployeeRemuneration,
                  totalMedicalExpense,
                  disallowableExpenses,
                  expenseCappedAt,
                  expenseCappedAmount,
                  notes,
                  reviewNotes
                );
                break;
              }
              case "Movement": {
                const {
                  openingBalance,
                  movementBalance,
                  closingBalance,
                  movementLines,
                  notes,
                  reviewNotes
                } = JSON.parse(jsonData);

                doc = generateMovementPdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo
                  },
                  company,
                  openingBalance,
                  movementBalance,
                  closingBalance,
                  movementLines,
                  notes,
                  reviewNotes
                );
                break;
              }
              case "Full_Breakdown": {
                const {
                  sourceData,
                  grossBalance,
                  adjustedTotalBalance,
                  totalSourceAmount,
                  lines,
                  notes,
                  reviewNotes
                } = JSON.parse(jsonData);

                doc = generateFullBreakdownPdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo
                  },
                  company,
                  sourceData,
                  grossBalance,
                  adjustedTotalBalance,
                  totalSourceAmount,
                  lines,
                  notes,
                  reviewNotes
                );
                break;
              }
              case "Partial_Breakdown": {
                const {
                  grossBalance,
                  adjustedTotalBalance,
                  lines,
                  notes,
                  reviewNotes
                } = JSON.parse(jsonData);

                doc = generatePartialBreakdownPdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo
                  },
                  company,
                  grossBalance,
                  adjustedTotalBalance,
                  lines,
                  notes,
                  reviewNotes
                );
                break;
              }
              case "Foreign_Trade":
              case "Other_Income": {
                const {
                  taxAdjustment,
                  otherIncomeRows,
                  expensesRows,
                  totalOtherIncome,
                  totalExpenses,
                  netOtherIncome,
                  foreignTaxSettings,
                  notes,
                  reviewNotes
                } = JSON.parse(jsonData);

                doc = generateOtherIncomePdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo,
                    taxAdjustment
                  },
                  company,
                  otherIncomeRows,
                  expensesRows,
                  totalOtherIncome,
                  totalExpenses,
                  netOtherIncome,
                  foreignTaxSettings,
                  notes,
                  reviewNotes
                );
                break;
              }
              case "Unremitted_Foreign_Income": {
                const {
                  taxAdjustment,
                  currentYearUnremittedForeignRows,
                  previousYearUnremittedForeignRows,
                  foreignSourceTrackingRows,
                  trackingSubtotals,
                  summaryData,
                  foreignTaxCreditScheduleRef,
                  notes,
                  reviewNotes
                } = JSON.parse(jsonData);
                doc = generateUnremittedForeignPdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo,
                    taxAdjustment
                  },
                  company,
                  currentYearUnremittedForeignRows,
                  previousYearUnremittedForeignRows,
                  foreignSourceTrackingRows,
                  trackingSubtotals,
                  summaryData,
                  foreignTaxCreditScheduleRef,
                  notes,
                  reviewNotes
                );
                break;
              }
              case "Foreign_Tax_Credit": {
                const {
                  taxAdjustment,
                  functionalCurrency,
                  functionalCurrencyRate,
                  breakdownRows,
                  foreignIncomeRows,
                  foreignIncomeTotals,
                  ftcCalculatedData,
                  notes,
                  reviewNotes
                } = JSON.parse(jsonData);

                doc = generateFtcPdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo,
                    taxAdjustment
                  },
                  company,
                  functionalCurrency,
                  functionalCurrencyRate,
                  breakdownRows,
                  foreignIncomeRows,
                  foreignIncomeTotals,
                  ftcCalculatedData,
                  notes,
                  reviewNotes
                );
                break;
              }
              case "Donation": {
                const {
                  donationRows,
                  totalDonation,
                  additionDeduction,
                  additionDeductionAmount,
                  notes,
                  reviewNotes
                } = JSON.parse(jsonData);

                doc = generateDonationPdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo
                  },
                  company,
                  donationRows,
                  totalDonation,
                  additionDeduction,
                  additionDeductionAmount,
                  notes,
                  reviewNotes
                );
                break;
              }
              case "Further_Deduction": {
                const {
                  expensesRows,
                  totalExpenses,
                  isExpenseCapped,
                  deductionRate,
                  expenseCappedAmount,
                  finalDeduction,
                  notes,
                  reviewNotes,
                  ...deductionDetails
                } = JSON.parse(jsonData);

                doc = generateFurtherDeductionPdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo
                  },
                  company,
                  expensesRows,
                  totalExpenses,
                  isExpenseCapped,
                  deductionRate,
                  expenseCappedAmount,
                  finalDeduction,
                  notes,
                  reviewNotes,
                  false,
                  deductionDetails
                );
                break;
              }
              case "Share_Based_Payment": {
                const scheduleData = JSON.parse(jsonData);
                const calculationResults =
                  calculateSharedBasedPayment(scheduleData);

                doc = generateSharedBasedPaymentPdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo
                  },
                  company,
                  scheduleData,
                  calculationResults,
                  scheduleData.notes,
                  scheduleData.reviewNotes,
                  false,
                  scheduleData.showFifoShares
                );
                break;
              }
              case "Allocation_Of_Expenses": {
                const {
                  incomeRows,
                  statutoryExpensesRows,
                  commonExpensesRows,
                  cappedRate,
                  expenseCappedAmount,
                  cappedVsCommon,
                  finalAllowedExpenses,
                  deductibleAllocationRows,
                  totalDeductibleExpenses,
                  notes,
                  reviewNotes
                } = JSON.parse(jsonData);

                doc = generateAllocationExpensesPdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo
                  },
                  company,
                  incomeRows,
                  statutoryExpensesRows,
                  commonExpensesRows,
                  cappedRate,
                  expenseCappedAmount,
                  cappedVsCommon,
                  finalAllowedExpenses,
                  deductibleAllocationRows,
                  totalDeductibleExpenses,
                  notes,
                  reviewNotes
                );
                break;
              }
              case "Other_Investment_Income": {
                const {
                  taxAdjustment,
                  investIncomeRows,
                  expensesRows,
                  totalInvestIncome,
                  totalExpenses,
                  netIncome,
                  foreignTaxSettings,
                  shareOfExpenses,
                  notes,
                  reviewNotes
                } = JSON.parse(jsonData);

                doc = generateInvestIncomePdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo,
                    taxAdjustment
                  },
                  company,
                  investIncomeRows,
                  expensesRows,
                  totalInvestIncome,
                  totalExpenses,
                  netIncome,
                  foreignTaxSettings,
                  shareOfExpenses,
                  notes,
                  reviewNotes
                );
                break;
              }
              case "Savy":
              case "Investment_Savy": {
                const { scheduleData, notes, reviewNotes, taxAdjustment } =
                  JSON.parse(jsonData);

                doc = generateSavvyPdf(
                  forView,
                  company,
                  {
                    name: scheduleName,
                    scheduleNo,
                    taxAdjustment
                  },
                  scheduleData,
                  notes,
                  reviewNotes
                );
                break;
              }
              case "Rental": {
                const {
                  taxAdjustment,
                  address,
                  hasVacantPeriod,
                  vacantPeriod,
                  rentalPeriod,
                  grossIncomeRows,
                  expensesRows,
                  totalGrossIncome,
                  netRentalIncome,
                  foreignTaxSettings,
                  notes,
                  reviewNotes,
                  shareOfExpenses
                } = JSON.parse(jsonData);

                doc = generateRentalIncomePdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo,
                    taxAdjustment
                  },
                  company,
                  address,
                  hasVacantPeriod,
                  vacantPeriod,
                  rentalPeriod,
                  grossIncomeRows,
                  expensesRows,
                  totalGrossIncome,
                  netRentalIncome,
                  foreignTaxSettings,
                  notes,
                  reviewNotes,
                  shareOfExpenses
                );
                break;
              }
              case "Renovation": {
                const {
                  rennovationRows,
                  mode,
                  isSdg,
                  tableRows,
                  secondTableRows,
                  totalRennovations,
                  totalQualifyingCost,
                  totalQualifyingCostBF,
                  totalCostIncurred,
                  totalDeduction,
                  totalCostCF,
                  totalSecondCostIncurred,
                  totalSecondQualifyingCost,
                  totalSecondQualifyingCostBF,
                  totalSecondDeduction,
                  totalSecondCostCF,
                  notes,
                  reviewNotes
                } = JSON.parse(jsonData);

                doc = generateRenovationPdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo
                  },
                  company,
                  rennovationRows,
                  mode,
                  isSdg,
                  tableRows,
                  secondTableRows,
                  totalRennovations,
                  totalQualifyingCost,
                  totalQualifyingCostBF,
                  totalCostIncurred,
                  totalDeduction,
                  totalCostCF,
                  totalSecondCostIncurred,
                  totalSecondQualifyingCost,
                  totalSecondQualifyingCostBF,
                  totalSecondDeduction,
                  totalSecondCostCF,
                  notes,
                  reviewNotes
                );
                break;
              }
              default:
                break;
            }
          }
        } catch (e) {
          if (onError)
            onError(
              `PL schedule ${scheduleNo} (${scheduleType})`,
              `Failed to generate PL schedule ${scheduleNo} (${scheduleType}): ${
                e?.message || e
              }`
            );
        }

        recordSavedDoc({
          doc,
          updatedAt,
          scheduleNo,
          scheduleType,
          scheduleName,
          zip,
          pdfBlobs
        });
      }
    }

    if (rawCaSchedules && rawCaSchedules.length > 0) {
      for (const {
        scheduleNo,
        scheduleName,
        scheduleType,
        updatedAt,
        jsonData
      } of rawCaSchedules) {
        let doc = null;
        try {
          if (isNotEmptyObject(jsonData)) {
            switch (scheduleType) {
              case "FASummary": {
                const {
                  costRows,
                  accDepreciationRows,
                  totalOpening,
                  totalAdditions,
                  totalDisposal,
                  totalClosing,
                  notes,
                  reviewNotes
                } = JSON.parse(jsonData);

                doc = generateFaSummaryPdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo
                  },
                  company,
                  costRows,
                  accDepreciationRows,
                  totalOpening,
                  totalAdditions,
                  totalDisposal,
                  totalClosing,
                  notes,
                  reviewNotes
                );
                break;
              }
              case "FARegister": {
                const { assetRows, dataRows, notes, reviewNotes } =
                  JSON.parse(jsonData);

                doc = generateFaRegisterPdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo
                  },
                  company,
                  assetRows || dataRows || [],
                  notes,
                  reviewNotes
                );
                break;
              }
              case "FAAdditions": {
                const {
                  assetLines,
                  otherSources,
                  registeredAssetSources,
                  totalLines,
                  totalAdditions,
                  otherSourcesTotalLines,
                  registeredAssetSourcesTotalLines,
                  combinedTotalLines,
                  filteredUsedColumns,
                  caSettings,
                  refSources,
                  notes,
                  reviewNotes,
                  renovationScheduleNo
                } = JSON.parse(jsonData);

                doc = generateFaAdditionsPdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo
                  },
                  company,
                  assetLines,
                  otherSources,
                  registeredAssetSources,
                  totalLines,
                  totalAdditions,
                  otherSourcesTotalLines,
                  registeredAssetSourcesTotalLines,
                  combinedTotalLines,
                  filteredUsedColumns,
                  caSettings,
                  refSources,
                  notes,
                  reviewNotes,
                  renovationScheduleNo
                );
                break;
              }
              case "FADisposals": {
                const {
                  assetLines,
                  totalAssetLines,
                  showNetbookValue,
                  twdvSummary,
                  bcSummary,
                  refSources = {},
                  notes,
                  reviewNotes
                } = JSON.parse(jsonData);

                doc = generateFaDisposalPdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo
                  },
                  company,
                  assetLines,
                  totalAssetLines,
                  showNetbookValue,
                  twdvSummary,
                  bcSummary,
                  refSources,
                  notes,
                  reviewNotes
                );
                break;
              }
              case "S19ASchedule":
              case "S19BSchedule":
              case "S19DSchedule":
              case "S16Schedule": {
                const {
                  caType,
                  refSources,
                  additionsDetails,
                  disposalDetails,
                  hirePurchaseAdditions,
                  hirePurchaseDisposal,
                  totalAdditionsDetails,
                  totalDisposalDetails,
                  totalHpAdditionsDetails,
                  totalHpDisposalDetails,
                  totalNetAA,
                  caSummaryDetails,
                  notes,
                  reviewNotes
                } = JSON.parse(jsonData);

                doc = generateCaSchedulePdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo
                  },
                  company,
                  caType,
                  refSources,
                  additionsDetails,
                  disposalDetails,
                  hirePurchaseAdditions,
                  hirePurchaseDisposal,
                  totalAdditionsDetails,
                  totalDisposalDetails,
                  totalHpAdditionsDetails,
                  totalHpDisposalDetails,
                  totalNetAA,
                  caSummaryDetails,
                  notes,
                  reviewNotes
                );
                break;
              }
              case "S19Schedule": {
                const {
                  caType,
                  refSources,
                  additionsDetails,
                  disposalDetails,
                  hirePurchaseAdditions,
                  hirePurchaseDisposal,
                  totalAdditionsDetails,
                  totalDisposalDetails,
                  totalHpAdditionsDetails,
                  totalHpDisposalDetails,
                  totalNetAA,
                  caSummaryDetails,
                  notes,
                  reviewNotes
                } = JSON.parse(jsonData);

                doc = generateS19SchedulePdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo
                  },
                  company,
                  caType,
                  refSources,
                  additionsDetails,
                  disposalDetails,
                  hirePurchaseAdditions,
                  hirePurchaseDisposal,
                  totalAdditionsDetails,
                  totalDisposalDetails,
                  totalHpAdditionsDetails,
                  totalHpDisposalDetails,
                  totalNetAA,
                  caSummaryDetails,
                  notes,
                  reviewNotes
                );
                break;
              }
              case "HirePurchase": {
                const {
                  assets,
                  assetCodeList,
                  summaryData,
                  caTypeTotals,
                  notes,
                  reviewNotes
                } = JSON.parse(jsonData);

                doc = generateHirePurchasePdf(
                  forView,
                  {
                    name: scheduleName,
                    scheduleNo
                  },
                  company,
                  assets,
                  assetCodeList,
                  summaryData,
                  caTypeTotals,
                  notes,
                  reviewNotes
                );
                break;
              }
              case "CASummary":
                doc = generateCaSummaryPdf(
                  forView,
                  {
                    type: "CA Summary",
                    scheduleNo: scheduleNo,
                    name: "CA Summary"
                  },
                  company,
                  jsonData,
                  jsonData.reduce((sum, row) => sum + row.total, 0),
                  "",
                  ""
                );
                break;
              default:
                break;
            }
          }
        } catch (e) {
          if (onError)
            onError(
              `CA schedule ${scheduleNo} (${scheduleType})`,
              `Failed to generate CA schedule ${scheduleNo} (${scheduleType}): ${
                e?.message || e
              }`
            );
        }

        recordSavedDoc({
          doc,
          updatedAt,
          scheduleNo,
          scheduleType,
          scheduleName,
          zip,
          pdfBlobs
        });
      }
    }

    if (forView) fileName = fileName + "_Draft";

    try {
      if (downloadType === "merged") {
        return await mergePDFs(pdfBlobs, fileName, {
          download: options.download !== false,
          contentPageCount,
          footer:
            options.footer ??
            (options.addDraftFooter
              ? { ...DEFAULT_PDF_FOOTER_OPTIONS, enabled: true }
              : DEFAULT_PDF_FOOTER_OPTIONS)
        });
      } else if (downloadType === "zip") {
        const zipBlob = await zip.generateAsync({ type: "blob" });

        const a = document.createElement("a");
        a.href = URL.createObjectURL(zipBlob);
        a.download = `${fileName}.zip`;
        a.click();
        return { blob: zipBlob, fileName: `${fileName}.zip` };
      }
    } catch (e) {
      console.error(
        `Failed to finalize download (${downloadType}): ${e?.message || e}`
      );
    }
  }
};
